import { InputAdornment, StandardTextFieldProps, TextField } from "@mui/material"
import { useEffect, useState } from "react"
import { countDecimals, roundDecimals } from "../../tools"

export interface NumberFieldProps extends Omit<StandardTextFieldProps, "onChange" | "value"> {
    value?: number | null
    disableArrows?: boolean
    onChange?: (value?: number) => void
    min?: number
    max?: number
    step?: number
    decimalCount?: number
    endAdornment?: React.ReactNode
}

export const NumberField = ({
    disableArrows = false,
    disabled,
    required,
    value,
    onChange,
    className,
    min,
    max,
    step,
    decimalCount,
    sx = [],
    endAdornment,
    ...props
}: NumberFieldProps) => {
    const [inputValue, setInputValue] = useState<number>()

    useEffect(() => {
        setInputValue(value ?? undefined)
    }, [value])

    const handleChange = (newValue?: number) => {
        if (!onChange) {
            return
        }
        if (newValue == null) {
            onChange(undefined)
            return
        }

        let savedValue: number | undefined = newValue
        // is a number
        if (decimalCount && countDecimals(newValue ?? 0) > decimalCount) {
            savedValue = roundDecimals(savedValue, decimalCount)
        }

        if ((max && savedValue > max) || (min && savedValue < min)) {
            // keep initial number
            savedValue = value ?? undefined
        }

        setInputValue(savedValue)
        onChange(savedValue)
    }

    return (
        <TextField
            variant="outlined"
            type="number"
            size="small"
            value={inputValue ?? ""}
            onChange={(e) => setInputValue(e.target.value === "" ? undefined : +e.target.value)}
            // only update on blur if input value and value don't match
            onBlur={() => inputValue !== value && handleChange(inputValue)}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    handleChange(inputValue)
                }
            }}
            helperText={required && "Required"}
            error={required && !inputValue}
            disabled={disabled}
            sx={[
                ...(Array.isArray(sx) ? sx : [sx]),
                disableArrows && {
                    "& input[type=number]": {
                        MozAppearance: "textfield",
                    },
                    "& [type=number]::-webkit-outer-spin-button, & [type=number]::-webkit-inner-spin-button": {
                        appearance: "none",
                        WebKitAppearance: "none",
                        margin: 0,
                    },
                },
            ]}
            className={className}
            {...props}
            slotProps={{
                input: {
                    endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment>,
                    ...props.slotProps?.input,
                },
                inputLabel: { shrink: true },
                htmlInput: { step: step ?? 0.1, min: min ?? 0, max, disabled, ...props.slotProps?.htmlInput },
                ...props.slotProps,
            }}
        />
    )
}
